import React, { useState } from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import { CircularProgress } from "@mui/material";
import uRLs from "../../baseUtils";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import Convertinvoice from "../../../components/pages/sale/ConvertInvoice";
import QRCode from "qrcode";
import InvoiceActionFooter from "../a4Invoices/common/InvoiceActionFooter";
import InvoiceInfoSidebar from "../a4Invoices/common/InvoiceInfoSidebar";
import InvoiceToggleActionCard from "../a4Invoices/common/InvoiceToggleActionCard";
import InvoicePreviewRenderer from "./common/InvoiceHA4PreviewRender";
import PaySettleModal from "./common/PaySettleModal ";
import InvoiceModalLayout from "./common/InvoiceModalLayout ";
import {
  capitalizeFirstLetter,
  joinAddress,
  loadImageDimensions,
  urlToBase64,
} from "./common/invoiceUtils";
import { useInvoiceData } from "./common/useInvoiceData";
import ReturnSaleInvoice from "./ReturnSaleInvoice";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import AxiosInstance from "../../AxiosInstance";
import handleError from "../../../constants/handleError";

const ServiceInvoice = ({
  isData,
  onClose,
  onDataUpdate,
  onDataChange,
  onListSaleRefresh,
  selectedOption,
  isActive,
  optData,
  setOptData,
}) => {
  const invoiceData = useInvoiceData({
    isData,
    onClose,
    onDataUpdate,
    onDataChange,
    optData,
    setOptData,
  });
  const {
    data,
    globalData,
    selectedTheme,
    selectedSize,
    qrCodeDataUrl,
    setSelectedSize,
    terms,
    latestVersion,
    actualPaidAmount,
    download,
    setDownload,
    billing,
    setBilling,
    selectTermalOption,
    setSelectTermalOption,
    selectHALFA4Option,
    setSelectHALFA4Option,
    isBtnLoading,
    setBtnLoading,
    isPaySettleModalOpen,
    setIsPaySettleModalOpen,
    isMainModalOpen,
    setIsMainModalOpen,
    saleLoading,
    closeAuto,
    setCloseAuto,
    showConfirm,
    setShowConfirm,
    handleResetSale,
    saleTime,
    storeSettings,
    isUser,
    handleSelectedSale,
    handlelatestSale,
    isBillReceipt,
    documentTitle,
    handleGenerateInvoice,
    handleModeConfirm,
  } = invoiceData;

  const [returnData, setIsReturnData] = useState("");
  const [isReturnView, setIsReturnView] = useState(false);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();

  const handleClose = () => {
    setIsMainModalOpen(false);
    setIsReturnView(false);
    onClose();
  };
  const handleSelectedReturnSale = async (sale) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/salereturn/${sale?._id}`,
      );
      setIsReturnData(response.data.results);
      setIsReturnView(true);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };
  const invLabel = isBillReceipt ? "Bill #:" : "Invoice #:";
  const invDate = isBillReceipt ? "Bill Date:" : "Invoice Date:";
  const invTimes = isBillReceipt ? "Bill Time:" : "Invoice Time:";

  const generateInvoice1 = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    let y = PADDING + 5;

    // --- 1. HEADER SECTION ---
    // Logo (Top Left)
    if (globalData?.salefrom?.image) {
      try {
        const imagePath = globalData?.imagePath;
        const base64 = await urlToBase64(imagePath);

        // 1. Get real dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 2. Define your maximum allowed size for the logo area
        const maxWidth = 30; // Slightly larger for better readability
        const maxHeight = 12;

        // 3. Calculate the ratio that fits without squeezing
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const finalWidth = width * ratio;
        const finalHeight = height * ratio;

        // 4. Center the image vertically in the 12-unit high 'maxHeight' space
        const adjustedY = y - 5 + (maxHeight - finalHeight) / 2;

        // 5. Add the image with dynamic dimensions
        doc.addImage(
          base64,
          "PNG",
          MARGIN_LEFT,
          adjustedY,
          finalWidth,
          finalHeight,
        );
      } catch (err) {
        // Fallback: If image fails, show company title
        console.warn("Logo failed to load:", err);
        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(14);
        doc.text(globalData?.salefrom?.title || "COMPANY LOGO", MARGIN_LEFT, y);
      }
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20, 40, 100);
    doc.text(documentTitle, MARGIN_RIGHT, y, { align: "right" });
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`${invLabel} ${globalData.saleno || ""}`, MARGIN_RIGHT, y, {
      align: "right",
    });

    // --- 2. SELLER DETAILS (Left) ---
    y += 6;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text(globalData?.salefrom?.title || "", MARGIN_LEFT, y);

    y += 5;
    doc.setFontSize(8);
    doc.text(`GSTIN ${globalData?.salefrom?.gstnumber || ""}`, MARGIN_LEFT, y);

    y += 4;
    doc.setFont("Inter-Regular", "normal");
    const sellerAddress = joinAddress(
      globalData?.salefrom?.addressline1,
      globalData?.salefrom?.addressline2,
      globalData?.salefrom?.district,
      globalData?.salefrom?.state,
      globalData?.salefrom?.pincode,
    );
    const sellerAddrLines = doc.splitTextToSize(sellerAddress, 70);
    doc.text(sellerAddrLines, MARGIN_LEFT, y);

    y += sellerAddrLines.length * 4;
    const drawBoldLabelNormalValue = (label, value, x, currentY) => {
      // 1. Set to Bold
      doc.setFont("helvetica", "bold");
      const labelText = `${label} `; // Removed colon if you want it exactly like the image
      doc.text(labelText, x, currentY);

      // 2. Measure the bold text width
      const labelWidth = doc.getTextWidth(labelText);

      // 3. Set to Normal
      doc.setFont("helvetica", "normal");
      doc.text(String(value || ""), x + labelWidth, currentY);
    };
    // drawBoldLabelNormalValue(
    //   "Mobile",
    //   globalData?.salefrom?.phone,
    //   MARGIN_LEFT,
    //   y
    // );
    const phone = globalData?.salefrom?.phone;
    const email = globalData?.salefrom?.email;
    y -= 1;
    if (phone || email) {
      let currentX = MARGIN_LEFT;
      const currentY = y;
      if (phone) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Mobile: ", currentX, currentY);

        currentX += doc.getTextWidth("Mobile: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(phone), currentX, currentY);

        currentX += doc.getTextWidth(String(phone));
      }
      if (phone && email) {
        doc.setFont("Inter-Regular", "normal");
        doc.text("  |  ", currentX, currentY);

        currentX += doc.getTextWidth("  |  ");
      }
      if (email) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Email: ", currentX, currentY);

        currentX += doc.getTextWidth("Email: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(email), currentX, currentY);
      }
    }
    if (globalData?.salefrom?.pannumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "PAN",
        globalData.salefrom.pannumber,
        MARGIN_LEFT,
        y,
      );
    }

    if (globalData?.salefrom?.fssai) {
      y += 4;
      drawBoldLabelNormalValue(
        "FSSAI",
        globalData.salefrom.fssai,
        MARGIN_LEFT,
        y,
      );
    }

    if (globalData?.salefrom?.cinnumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "CIN",
        globalData.salefrom.cinnumber,
        MARGIN_LEFT,
        y,
      );
    }

    const hasCustomer = !!globalData?.saleto;
    const hasDate = !!globalData?.saledate;
    const hasSupply = !!globalData?.saleto?.state;

    let hasBillSection = hasCustomer || hasDate || hasSupply;
    const hasShipping =
      globalData.saleto?.shippingAddress &&
      (globalData?.saleto?.shippingAddress?.addressline1 ||
        globalData?.saleto?.shippingAddress?.addressline2);

    const shipColX = 100; // center column
    const rightColX = 160;
    if (hasBillSection) {
      y += 6;

      const col2X = 140;

      /* --- LEFT: BILL TO --- */
      if (hasCustomer && optData?.isCustomerDisplay) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Bill To:", MARGIN_LEFT, y);
        let leftY = y + 4;
        const nameLines = doc.splitTextToSize(
          globalData?.saleto?.name || "",
          80,
        );
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(globalData?.saleto?.name || "", MARGIN_LEFT, leftY);
        leftY += nameLines.length * 4;

        if (globalData?.saleto?.gstnumber) {
          doc.text(`GSTIN: ${globalData.saleto.gstnumber}`, MARGIN_LEFT, leftY);
          leftY += 4;
        }

        // Address
        const custAddress = [
          globalData?.saleto?.addressline1,
          globalData?.saleto?.addressline2,
          globalData?.saleto?.district,
          globalData?.saleto?.state,
          globalData?.saleto?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        // let leftY = y + 12;

        if (custAddress) {
          const lines = doc.splitTextToSize(custAddress, 80);
          doc.setFont("Inter-Regular", "normal");
          doc.text(lines, MARGIN_LEFT, leftY);
          leftY += lines.length * 4;
        }
        leftY = leftY + 0.5;
        // Contact
        if (globalData?.saleto?.phone || globalData?.saleto?.email) {
          const contact = `Ph: ${globalData?.saleto?.phone || ""} ${
            globalData?.saleto?.email
              ? "| Email: " + globalData.saleto.email
              : ""
          }`;
          doc.text(contact, MARGIN_LEFT, leftY);
          leftY += 4;
        }
        if (globalData?.saleto?.referenceid) {
          doc.setFont("helvetica", "normal");
          doc.text(
            `Reference No: ${globalData.saleto.referenceid}`,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (globalData?.saleto?.pannumber) {
          drawBoldLabelNormalValue(
            "PAN",
            globalData.saleto.pannumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (globalData?.saleto?.fssai) {
          drawBoldLabelNormalValue(
            "FSSAI",
            globalData.saleto.fssai,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (globalData?.saleto?.cinnumber) {
          drawBoldLabelNormalValue(
            "CIN",
            globalData.saleto.cinnumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }
        var leftEndY = leftY;
      }
      /* --- CENTER: SHIPPING ADDRESS --- */
      let shipEndY = y;

      if (hasShipping && optData?.isCustomerDisplay) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Shipping Address:", shipColX, y);

        const shipAddress = [
          globalData?.saleto?.shippingAddress?.addressline1,
          globalData?.saleto?.shippingAddress?.addressline2,
          globalData?.saleto?.shippingAddress?.district,
          globalData?.saleto?.shippingAddress?.state,
          globalData?.saleto?.shippingAddress?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let shipY = y + 4;

        if (shipAddress) {
          const shipLines = doc.splitTextToSize(shipAddress, 50);
          doc.setFont("Inter-Regular", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(shipLines, shipColX, shipY);

          shipY += shipLines.length * 4;
        }

        shipEndY = shipY;
      }
      /* --- RIGHT: DATE / TIME / SUPPLY --- */
      let rightY = y;

      if (hasDate) {
        doc.setFont("Inter-Bold", "bold");
        doc.text(invDate, rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(
          DateConverter.ISOToDisplayFormat(globalData?.saledate).date,
          MARGIN_RIGHT,
          rightY,
          { align: "right" },
        );

        rightY += 4;

        doc.setFont("Inter-Bold", "bold");
        doc.text(invTimes, rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        const invTime = DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(
            globalData?.saledate,
            isUser.timezone,
          ),
        ).time;

        doc.text(invTime, MARGIN_RIGHT, rightY, { align: "right" });

        rightY += 4;
      }

      if (hasSupply) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Place of Supply:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(globalData.saleto.state, MARGIN_RIGHT, rightY, {
          align: "right",
        });

        rightY += 4;
      }

      /* --- FINAL HEIGHT FIX --- */
      const finalY = Math.max(leftEndY || y, shipEndY || y, rightY);

      y = finalY + 2;
    }
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: false,
        textColor: "#000",
        //  textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
        lineWidth: { bottom: 0.5 },
        lineColor: selectedTheme?.templateTheme?.borderColor,
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "left" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 30, halign: "right" },
        4: { cellWidth: 20, halign: "right" },
        5: { cellWidth: 25, halign: "right" },
      },
      head: [["#", "Item", "MRP", "Selling Price", "Qty", "Amount"]],
      body: globalData.saleitems.map((it, i) => [
        i + 1,
        it.hsnsac
          ? `${it.title}\n${it.imode === "SERVICE" ? "SAC:" : "HSN:"} ${it.hsnsac}`
          : it.title,
        `₹${it.mrp}`,
        `₹${it.baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `₹${it.lineGrandTotal}`,
      ]),
      didParseCell: function (data) {
        // Header alignment
        if (data.section === "head") {
          const colId = data.column.index;
          if (colId === 0) data.cell.styles.halign = "left";
          if (
            colId === 2 ||
            colId === 3 ||
            colId === 4 ||
            colId === 5 ||
            colId === 6
          )
            data.cell.styles.halign = "right";
        }
        if (data.section === "body") {
          // Light gray divider lines
          data.cell.styles.lineWidth = { bottom: 0.1 };
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;

    if (y + PADDING > pageH - 20) {
      doc.addPage();
      y = PADDING + 5;
    }
    const footerTop = y;
    let leftY = footerTop;
    let rightY = footerTop;

    // --- 1. LEFT COLUMN: Bank Details ---
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    if (storeSettings?.paymentSettings?.bankDetails && optData?.isBankDetails) {
      doc.text("Bank Details:", MARGIN_LEFT, leftY);
      leftY += 5;

      const bankInfo = [
        [
          "Name:",
          storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "",
        ],
        [
          "Account #:",
          storeSettings?.paymentSettings?.bankDetails?.accountNo || "",
        ],
        ["IFSC:", storeSettings?.paymentSettings?.bankDetails?.ifsc || ""],
        ["UPI ID:", storeSettings?.paymentSettings?.upiId || ""],
      ];

      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.text(label, MARGIN_LEFT, leftY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(String(value || ""), MARGIN_LEFT + 20, leftY);
        leftY += 4;
      });
    }
    const upiId = storeSettings?.paymentSettings?.upiId || "";
    doc.setFontSize(8);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
      storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "",
    )}&cu=INR`;
    const qrX = pageW / 2 - 15;
    if (optData?.isBankDetails) {
      if (upiId && upiId.trim() !== "") {
        const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
          errorCorrectionLevel: "L",
          scale: 4,
          margin: 1,
        });
        doc.addImage(qrCodeDataUrl, "JPEG", qrX, footerTop - 3, 20, 20);
      }
    }
    const summaryX = 140;

    // Row 1: Service Charges (Normal Font)
    // doc.setFont("Inter-Regular", "normal");
    // doc.text("Service Charges", summaryX, rightY);
    // doc.setTextColor(0);
    // doc.text(formatCurrency(data.serviceCharges || 0), MARGIN_RIGHT, rightY, { align: "right" });
    // rightY += 4;

    // // SAC Code (Small font under Service Charges)
    // doc.setFontSize(7);
    // doc.text("SAC:", summaryX + 10, rightY); // Offset slightly to the right
    // rightY += 5;
    const isInterState =
      globalData?.salefrom?.statecode &&
      globalData?.saleto?.statecode &&
      globalData?.salefrom?.statecode !== globalData?.saleto?.statecode;

    // Rows 2-4: Taxable & GST (Bold Font)
    const summaryItems = [["Taxable Amount", `₹${globalData?.totalbaseprice}`]];
    if (isInterState) {
      summaryItems.push(["IGST ", `₹${globalData?.igstamount}`]);
    } else {
      summaryItems.push(["CGST ", `₹${globalData?.cgstamount}`]);
      summaryItems.push(["SGST ", `₹${globalData?.sgstamount}`]);
    }

    doc.setFontSize(9);
    summaryItems.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setTextColor(0);
      doc.text(label, summaryX, rightY);
      doc.text(value, MARGIN_RIGHT, rightY, { align: "right" });
      rightY += 5;
    });
    rightY += 2;
    // --- 4. THE TOTAL BOX ---
    const internalPadding = 2; // Adjust this value to get the exact gap you want
    doc.setFillColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setTextColor(selectedTheme?.templateTheme?.foregroundColor);
    doc.setLineWidth(0.4);
    doc.rect(summaryX - 2, rightY - 4, MARGIN_RIGHT - summaryX + 2, 8, "FD");
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(11);
    doc.text("Total", summaryX, rightY + 1.5);
    doc.text(
      `₹${globalData.grandtotal}`,
      MARGIN_RIGHT - internalPadding, // This moves the amount away from the line
      rightY + 1.5,
      { align: "right" },
    );
    // doc.text(formatCurrency((CurrencyConverter.toCurrency(data.grandtotal|| 0, "INR")), "INR", true, true), MARGIN_RIGHT, rightY + 1.5, { align: "right" });
    rightY += 8;
    if (leftY > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }
    const paidamount =
      (globalData?.grandtotal || 0) - (globalData?.balanceDue || 0);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text("Amount Payable:", summaryX, rightY);
    doc.text(`₹${globalData?.balanceDue}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    rightY += 5;
    doc.setFont("Inter-Regular", "normal");
    doc.text("Amount Paid:", summaryX, rightY);
    doc.text(`${paidamount.toFixed(2)}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const amountWords = `Total amount (in words) ${
      globalData?.amountinwords || ""
    }`;
    // doc.text(amountWords, MARGIN_LEFT, wordsY);
    const wordLines = doc.splitTextToSize(amountWords, qrX + 25);
    doc.text(wordLines, MARGIN_LEFT, leftY + 2);

    leftY += wordLines.length * 4 + 3;

    let paymentMethodsHeight = 0;
    if (optData?.isPayment) {
      if (globalData?.cashPaid > 0) paymentMethodsHeight += 4;
      if (globalData?.upiPaid > 0) paymentMethodsHeight += 4;
      if (globalData?.cardPaid > 0) paymentMethodsHeight += 4;
      if (globalData?.onlinePaid > 0) paymentMethodsHeight += 4;
    }
    const totalNeededHeight = paymentMethodsHeight + wordLines.length * 4 + 10;
    if (leftY + totalNeededHeight > pageH - 20) {
      doc.addPage();
      leftY = PADDING + 5;
      rightY = PADDING + 5;
    }
    const payments = [
      { amount: globalData?.cashPaid, label: "Cash" },
      { amount: globalData?.upiPaid, label: "UPI" },
      { amount: globalData?.cardPaid, label: "Card" },
      { amount: globalData?.onlinePaid, label: "Online" },
    ].filter((p) => p.amount > 0);

    const drawPayment = (amount, label, isLast) => {
      if (amount > 0) {
        let x = MARGIN_LEFT;

        // 👉 Bold Amount
        doc.setFont("Inter-Bold", "bold");
        const amountText = `₹${amount} `;
        doc.text(amountText, x, leftY);

        x += doc.getTextWidth(amountText);

        // 👉 Normal Text
        doc.setFont("Inter-Regular", "normal");
        doc.text(`Paid via ${label}`, x, leftY);

        // 👉 ONLY add spacing if NOT last
        if (!isLast) {
          leftY += 4;
        }
      }
    };
    if (optData?.isPayment) {
      payments.forEach((p, index) => {
        const isLast = index === payments.length - 1;
        drawPayment(p.amount, p.label, isLast);
      });
    }
    doc.setFont("Inter-Regular", "normal");

    if (optData?.isPayment) {
      y = Math.max(leftY, rightY) + 8;
    } else {
      y = Math.max(leftY, rightY + 10);
    }
    if (y > pageH - 40) {
      doc.addPage();
      y = PADDING + 5;
    }

    let rowStartY = y;
    if (optData?.isRemark && globalData?.remark) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      if (rowStartY > pageH - 30) {
        doc.addPage();
        rowStartY = PADDING;
      }

      doc.text("Remark", PADDING, rowStartY);

      const innerPaddingX = 2;

      // Kept your text width constraint (Note: removing +25 ensures text doesn't overflow the physical box boundary)
      const maxTextWidth = pageW - PADDING * 2 - innerPaddingX * 2 + 25;

      const remarkLines = doc.splitTextToSize(
        capitalizeFirstLetter(globalData.remark),
        maxTextWidth,
      );

      // Tightened Line Height and Padding
      const textLineHeight = 4.0;
      const boxPaddingTop = 2.5; // Reduced top padding
      const boxPaddingBottom = 2.5; // Reduced bottom padding

      // Calculate exact container height
      const remarkHeight =
        boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;
      const boxStartY = rowStartY + 2.5;

      // Draw the tight rounded container box
      doc.setDrawColor(180);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);
      doc.setTextColor(0);

      // Tight baseline: places the first line's baseline perfectly within the box padding
      let remarkY = boxStartY + boxPaddingTop;

      remarkLines.forEach((line) => {
        if (remarkY > pageH - PADDING) {
          doc.addPage();
          remarkY = PADDING + 6;
        }

        doc.text(line, PADDING, remarkY);
        remarkY += textLineHeight;
      });

      // Update rowStartY safely below the tight box
      rowStartY = boxStartY + remarkHeight + 2;
    }
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      // Page break check
      if (rowStartY > pageH - 25) {
        doc.addPage();
        rowStartY = PADDING + 5;
      }

      doc.text("Terms & Conditions", PADDING, rowStartY);
      doc.setTextColor(0);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW - 50); // Limit width to half page
      let currentTermY = rowStartY + 5;

      termLines.forEach((line) => {
        if (currentTermY > pageH - 20) {
          doc.addPage();
          currentTermY = 10;
        }
        doc.text(line, PADDING, currentTermY);
        currentTermY += 5;
      });

      // Update global y based on how long the terms were
      y = currentTermY;
    }

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 35;
        const maxHeight = 20;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          MARGIN_RIGHT - imgW,
          rowStartY, // Start at the same Y as the Terms header
          imgW,
          imgH,
        );
      } catch {
        console.warn("Signature load failed");
      }
    }
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);
    doc.text("Authorized Signatory", MARGIN_RIGHT, rowStartY + 13, {
      align: "right",
    });
    if (storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW / 2 - 10;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");

      // Start X so whole string is right-aligned
      const startX = rightX;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y, {
        align: "center",
      });

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + 1 + normalW, y, {
        align: "center",
      });
    }
    setDownload(false);
    return doc;
  };

  const generateBill1 = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const PADDING = 5;
    const MARGIN_LEFT = PADDING;
    const MARGIN_RIGHT = pageW - PADDING;
    let y = PADDING + 5;

    // --- 1. HEADER SECTION ---
    // Logo (Top Left)
    if (globalData?.salefrom?.image) {
      try {
        const imagePath = globalData?.imagePath;
        const base64 = await urlToBase64(imagePath);

        // 1. Get real dimensions
        const { width, height } = await loadImageDimensions(base64);

        // 2. Define your maximum allowed size for the logo area
        const maxWidth = 30; // Slightly larger for better readability
        const maxHeight = 12;

        // 3. Calculate the ratio that fits without squeezing
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const finalWidth = width * ratio;
        const finalHeight = height * ratio;

        // 4. Center the image vertically in the 12-unit high 'maxHeight' space
        const adjustedY = y - 5 + (maxHeight - finalHeight) / 2;

        // 5. Add the image with dynamic dimensions
        doc.addImage(
          base64,
          "PNG",
          MARGIN_LEFT,
          adjustedY,
          finalWidth,
          finalHeight,
        );
      } catch (err) {
        // Fallback: If image fails, show company title
        console.warn("Logo failed to load:", err);
        doc.setFont("Inter-Bold", "bold");
        doc.setFontSize(14);
        doc.text(globalData?.salefrom?.title || "COMPANY LOGO", MARGIN_LEFT, y);
      }
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20, 40, 100);
    doc.text("BILL RECEIPT", MARGIN_RIGHT, y, { align: "right" });
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`Bill #: ${globalData.saleno || ""}`, MARGIN_RIGHT, y, {
      align: "right",
    });

    // --- 2. SELLER DETAILS (Left) ---
    y += 6;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text(globalData?.salefrom?.title || "", MARGIN_LEFT, y);

    y += 5;
    doc.setFontSize(8);
    doc.text(`GSTIN ${globalData?.salefrom?.gstnumber || ""}`, MARGIN_LEFT, y);

    y += 4;
    doc.setFont("Inter-Regular", "normal");
    const sellerAddress = joinAddress(
      globalData?.salefrom?.addressline1,
      globalData?.salefrom?.addressline2,
      globalData?.salefrom?.district,
      globalData?.salefrom?.state,
      globalData?.salefrom?.pincode,
    );
    const sellerAddrLines = doc.splitTextToSize(sellerAddress, 70);
    doc.text(sellerAddrLines, MARGIN_LEFT, y);

    y += sellerAddrLines.length * 4;
    const drawBoldLabelNormalValue = (label, value, x, currentY) => {
      // 1. Set to Bold
      doc.setFont("helvetica", "bold");
      const labelText = `${label} `; // Removed colon if you want it exactly like the image
      doc.text(labelText, x, currentY);

      // 2. Measure the bold text width
      const labelWidth = doc.getTextWidth(labelText);

      // 3. Set to Normal
      doc.setFont("helvetica", "normal");
      doc.text(String(value || ""), x + labelWidth, currentY);
    };
    // drawBoldLabelNormalValue(
    //   "Mobile",
    //   globalData?.salefrom?.phone,
    //   MARGIN_LEFT,
    //   y
    // );
    const phone = globalData?.salefrom?.phone;
    const email = globalData?.salefrom?.email;
    y -= 1;
    if (phone || email) {
      let currentX = MARGIN_LEFT;
      const currentY = y;
      if (phone) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Mobile: ", currentX, currentY);

        currentX += doc.getTextWidth("Mobile: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(phone), currentX, currentY);

        currentX += doc.getTextWidth(String(phone));
      }
      if (phone && email) {
        doc.setFont("Inter-Regular", "normal");
        doc.text("  |  ", currentX, currentY);

        currentX += doc.getTextWidth("  |  ");
      }
      if (email) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Email: ", currentX, currentY);

        currentX += doc.getTextWidth("Email: ");

        doc.setFont("Inter-Regular", "normal");
        doc.text(String(email), currentX, currentY);
      }
    }
    if (globalData?.salefrom?.pannumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "PAN",
        globalData.salefrom.pannumber,
        MARGIN_LEFT,
        y,
      );
    }

    if (globalData?.salefrom?.fssai) {
      y += 4;
      drawBoldLabelNormalValue(
        "FSSAI",
        globalData.salefrom.fssai,
        MARGIN_LEFT,
        y,
      );
    }

    if (globalData?.salefrom?.cinnumber) {
      y += 4;
      drawBoldLabelNormalValue(
        "CIN",
        globalData.salefrom.cinnumber,
        MARGIN_LEFT,
        y,
      );
    }

    // --- 3. BILL TO & DATES ---
    const hasCustomer = !!globalData?.saleto;
    const hasDate = !!globalData?.saledate;
    const hasSupply = !!globalData?.saleto?.state;

    let hasBillSection = hasCustomer || hasDate || hasSupply;
    const hasShipping =
      globalData.saleto?.shippingAddress &&
      (globalData?.saleto?.shippingAddress?.addressline1 ||
        globalData?.saleto?.shippingAddress?.addressline2);

    const shipColX = 100; // center column
    const rightColX = 160;
    if (hasBillSection) {
      y += 6;

      const col2X = 140;

      /* --- LEFT: BILL TO --- */
      if (hasCustomer && optData?.isCustomerDisplay) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Bill To:", MARGIN_LEFT, y);

        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(globalData?.saleto?.name || "", MARGIN_LEFT, y + 4);

        if (globalData?.saleto?.gstnumber) {
          doc.text(`GSTIN: ${globalData.saleto.gstnumber}`, MARGIN_LEFT, y + 8);
        }

        // Address
        const custAddress = [
          globalData?.saleto?.addressline1,
          globalData?.saleto?.addressline2,
          globalData?.saleto?.district,
          globalData?.saleto?.state,
          globalData?.saleto?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let leftY = y + 12;

        if (custAddress) {
          const lines = doc.splitTextToSize(custAddress, 80);
          doc.setFont("Inter-Regular", "normal");
          doc.text(lines, MARGIN_LEFT, leftY);
          leftY += lines.length * 4;
        }
        leftY -= 1;
        // Contact
        if (globalData?.saleto?.phone || globalData?.saleto?.email) {
          const contact = `Ph: ${globalData?.saleto?.phone || ""} ${
            globalData?.saleto?.email
              ? "| Email: " + globalData.saleto.email
              : ""
          }`;
          doc.text(contact, MARGIN_LEFT, leftY);
          leftY += 4;
        }
        if (globalData?.saleto?.referenceid) {
          doc.setFont("helvetica", "normal");
          doc.text(
            `Reference No: ${globalData.saleto.referenceid}`,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }
        if (globalData?.saleto?.pannumber) {
          drawBoldLabelNormalValue(
            "PAN",
            globalData.saleto.pannumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (globalData?.saleto?.fssai) {
          drawBoldLabelNormalValue(
            "FSSAI",
            globalData.saleto.fssai,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }

        if (globalData?.saleto?.cinnumber) {
          drawBoldLabelNormalValue(
            "CIN",
            globalData.saleto.cinnumber,
            MARGIN_LEFT,
            leftY,
          );
          leftY += 4;
        }
        var leftEndY = leftY;
      }
      /* --- CENTER: SHIPPING ADDRESS --- */
      let shipEndY = y;

      if (hasShipping && optData?.isCustomerDisplay) {
        doc.setFont("Inter-Bold", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Shipping Address:", shipColX, y);

        const shipAddress = [
          globalData?.saleto?.shippingAddress?.addressline1,
          globalData?.saleto?.shippingAddress?.addressline2,
          globalData?.saleto?.shippingAddress?.district,
          globalData?.saleto?.shippingAddress?.state,
          globalData?.saleto?.shippingAddress?.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        let shipY = y + 4;

        if (shipAddress) {
          const shipLines = doc.splitTextToSize(shipAddress, 50);
          doc.setFont("Inter-Regular", "normal");
          doc.setTextColor(0, 0, 0);
          doc.text(shipLines, shipColX, shipY);

          shipY += shipLines.length * 4;
        }

        shipEndY = shipY;
      }
      /* --- RIGHT: DATE / TIME / SUPPLY --- */
      let rightY = y;

      if (hasDate) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Bill Date:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(
          DateConverter.ISOToDisplayFormat(globalData?.saledate).date,
          MARGIN_RIGHT,
          rightY,
          { align: "right" },
        );

        rightY += 4;

        doc.setFont("Inter-Bold", "bold");
        doc.text("Bill Time:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        const invTime = DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(
            globalData?.saledate,
            isUser.timezone,
          ),
        ).time;

        doc.text(invTime, MARGIN_RIGHT, rightY, { align: "right" });

        rightY += 4;
      }

      if (hasSupply) {
        doc.setFont("Inter-Bold", "bold");
        doc.text("Place of Supply:", rightColX, rightY);

        doc.setFont("Inter-Regular", "normal");
        doc.text(globalData.saleto.state, MARGIN_RIGHT, rightY, {
          align: "right",
        });

        rightY += 4;
      }

      /* --- FINAL HEIGHT FIX --- */
      const finalY = Math.max(leftEndY || y, shipEndY || y, rightY);

      y = finalY + 2;
    }
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { top: PADDING, left: PADDING, right: PADDING, bottom: PADDING }, // Balanced
      styles: {
        font: "Inter-Regular",
        fontSize: 8,
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        textColor: [0, 0, 0],
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: false,
        textColor: "#000",
        //  textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0], // White text
        lineWidth: { bottom: 0.5 },
        lineColor: selectedTheme?.templateTheme?.borderColor,
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "left" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 30, halign: "right" },
        3: { cellWidth: 20, halign: "right" },
        4: { cellWidth: 25, halign: "right" },
      },
      head: [["#", "Item", "Selling Price", "Qty", "Amount"]],
      body: globalData?.saleitems.map((it, i) => [
        i + 1,
        it.hsnsac
          ? `${it.title}\n${it.imode === "SERVICE" ? "SAC:" : "HSN:"} ${it.hsnsac}`
          : it.title,
        `₹${it.baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode  || "";
        })()}`,
        `₹${it.lineGrandTotal}`,
      ]),
      didParseCell: function (data) {
        // Header alignment
        if (data.section === "head") {
          const colId = data.column.index;
          if (colId === 0) data.cell.styles.halign = "left";
          if (colId === 2 || colId === 3 || colId === 4 || colId === 5)
            data.cell.styles.halign = "right";
        }
        if (data.section === "body") {
          // Light gray divider lines
          data.cell.styles.lineWidth = { bottom: 0.1 };
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;
    if (y + PADDING > pageH - 20) {
      doc.addPage();
      y = PADDING + 5;
    }
    const footerTop = y;

    let leftY = footerTop;
    let rightY = footerTop;

    // --- 1. LEFT COLUMN: Bank Details ---
    doc.setFontSize(9);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    if (storeSettings?.paymentSettings?.bankDetails && optData?.isBankDetails) {
      doc.text("Bank Details:", MARGIN_LEFT, leftY);
      leftY += 5;

      const bankInfo = [
        [
          "Name:",
          storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "",
        ],
        [
          "Account #:",
          storeSettings?.paymentSettings?.bankDetails?.accountNo || "",
        ],
        ["IFSC:", storeSettings?.paymentSettings?.bankDetails?.ifsc || ""],
        ["UPI ID:", storeSettings?.paymentSettings?.upiId || ""],
      ];

      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.text(label, MARGIN_LEFT, leftY);
        doc.setFont("Inter-Regular", "normal");
        doc.text(String(value || ""), MARGIN_LEFT + 20, leftY);
        leftY += 4;
      });
    }

    const upiId = storeSettings?.paymentSettings?.upiId || "";
    doc.setFontSize(8);
    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
      storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "",
    )}&cu=INR`;
    const qrX = pageW / 2 - 15;
    if (optData?.isBankDetails) {
      if (upiId && upiId.trim() !== "") {
        const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
          errorCorrectionLevel: "L",
          scale: 4,
          margin: 1,
        });
        doc.addImage(qrCodeDataUrl, "JPEG", qrX, footerTop - 3, 20, 20);
      }
    }
    const summaryX = 140;
    // Rows 2-4: Taxable & GST (Bold Font)
    const summaryItems = [["Taxable Amount", `₹${globalData?.totalbaseprice}`]];

    doc.setFontSize(9);
    summaryItems.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setTextColor(0);
      doc.text(label, summaryX, rightY);
      doc.text(value, MARGIN_RIGHT, rightY, { align: "right" });
      rightY += 5;
    });
    rightY += 2;
    // --- 4. THE TOTAL BOX ---
    doc.setFillColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    doc.setTextColor(selectedTheme?.templateTheme?.foregroundColor);
    doc.setLineWidth(0.4);
    doc.rect(summaryX - 2, rightY - 4, MARGIN_RIGHT - summaryX + 2, 8, "FD");

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(11);
    doc.text("Total", summaryX, rightY + 1.5);
    doc.text(
      `₹${globalData?.grandtotal}`,
      MARGIN_RIGHT - 2, // This moves the amount away from the line
      rightY + 1.5,
      { align: "right" },
    );
    rightY += 8;
    const paidamount =
      (globalData?.grandtotal || 0) - (globalData?.balanceDue || 0);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text("Amount Payable:", summaryX, rightY);
    doc.text(`₹${globalData?.balanceDue}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    rightY += 5;
    doc.setFont("Inter-Regular", "normal");
    doc.text("Amount Paid:", summaryX, rightY);
    doc.text(`${paidamount.toFixed(2)}`, MARGIN_RIGHT, rightY, {
      align: "right",
    });
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const amountWords = `Total amount (in words) ${
      globalData?.amountinwords || ""
    }`;
    // doc.text(amountWords, MARGIN_LEFT, wordsY);
    const wordLines = doc.splitTextToSize(amountWords, qrX + 25);
    doc.text(wordLines, MARGIN_LEFT, leftY + 2);

    leftY += wordLines.length * 4 + 3;

    let paymentMethodsHeight = 0;
    if (optData?.isPayment) {
      if (globalData?.cashPaid > 0) paymentMethodsHeight += 4;
      if (globalData?.upiPaid > 0) paymentMethodsHeight += 4;
      if (globalData?.cardPaid > 0) paymentMethodsHeight += 4;
      if (globalData?.onlinePaid > 0) paymentMethodsHeight += 4;

      const totalNeededHeight =
        paymentMethodsHeight + wordLines.length * 4 + 10;
      if (leftY + totalNeededHeight > pageH - 20) {
        doc.addPage();
        leftY = PADDING + 5;
        rightY = PADDING + 5;
      }
    }
    const payments = [
      { amount: globalData?.cashPaid, label: "Cash" },
      { amount: globalData?.upiPaid, label: "UPI" },
      { amount: globalData?.cardPaid, label: "Card" },
      { amount: globalData?.onlinePaid, label: "Online" },
    ].filter((p) => p.amount > 0);

    const drawPayment = (amount, label, isLast) => {
      if (amount > 0) {
        let x = MARGIN_LEFT;

        // 👉 Bold Amount
        doc.setFont("Inter-Bold", "bold");
        const amountText = `₹${amount} `;
        doc.text(amountText, x, leftY);

        x += doc.getTextWidth(amountText);

        // 👉 Normal Text
        doc.setFont("Inter-Regular", "normal");
        doc.text(`Paid via ${label}`, x, leftY);

        // 👉 ONLY add spacing if NOT last
        if (!isLast) {
          leftY += 4;
        }
      }
    };
    if (optData?.isPayment) {
      payments.forEach((p, index) => {
        const isLast = index === payments.length - 1;
        drawPayment(p.amount, p.label, isLast);
      });
    }

    doc.setFont("Inter-Regular", "normal");

    if (optData?.isPayment) {
      y = Math.max(leftY, rightY) + 8;
    } else {
      y = Math.max(leftY, rightY + 10);
    }
    if (y > pageH - 40) {
      doc.addPage();
      y = PADDING + 5;
    }
    let rowStartY = y;
    if (optData?.isRemark && globalData?.remark) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      if (rowStartY > pageH - 30) {
        doc.addPage();
        rowStartY = PADDING;
      }

      doc.text("Remark", PADDING, rowStartY);

      const innerPaddingX = 2;

      // Kept your text width constraint (Note: removing +25 ensures text doesn't overflow the physical box boundary)
      const maxTextWidth = pageW - PADDING * 2 - innerPaddingX * 2 + 25;

      const remarkLines = doc.splitTextToSize(
        capitalizeFirstLetter(globalData.remark),
        maxTextWidth,
      );

      // Tightened Line Height and Padding
      const textLineHeight = 4.0;
      const boxPaddingTop = 2.5; // Reduced top padding
      const boxPaddingBottom = 2.5; // Reduced bottom padding

      // Calculate exact container height
      const remarkHeight =
        boxPaddingTop + remarkLines.length * textLineHeight + boxPaddingBottom;
      const boxStartY = rowStartY + 2.5;

      // Draw the tight rounded container box
      doc.setDrawColor(180);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(8);
      doc.setTextColor(0);

      // Tight baseline: places the first line's baseline perfectly within the box padding
      let remarkY = boxStartY + boxPaddingTop;

      remarkLines.forEach((line) => {
        if (remarkY > pageH - PADDING) {
          doc.addPage();
          remarkY = PADDING + 6;
        }

        doc.text(line, PADDING, remarkY);
        remarkY += textLineHeight;
      });

      // Update rowStartY safely below the tight box
      rowStartY = boxStartY + remarkHeight + 2;
    }
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.setTextColor(72, 72, 72);

      // Page break check
      if (rowStartY > pageH - 25) {
        doc.addPage();
        rowStartY = PADDING + 5;
      }

      doc.text("Terms & Conditions", PADDING, rowStartY);
      doc.setTextColor(0);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);

      const termLines = doc.splitTextToSize(terms, pageW - 50); // Limit width to half page
      let currentTermY = rowStartY + 5;

      termLines.forEach((line) => {
        if (currentTermY > pageH - 20) {
          doc.addPage();
          currentTermY = 10;
        }
        doc.text(line, PADDING, currentTermY);
        currentTermY += 5;
      });

      // Update global y based on how long the terms were
      y = currentTermY;
    }

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const signimg =
          uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
        const base64 = await urlToBase64(signimg);
        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 35;
        const maxHeight = 20;
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgW = width * ratio;
        const imgH = height * ratio;

        doc.addImage(
          base64,
          signimg.endsWith(".png") ? "PNG" : "JPEG",
          MARGIN_RIGHT - imgW,
          rowStartY, // Start at the same Y as the Terms header
          imgW,
          imgH,
        );
      } catch {
        console.warn("Signature load failed");
      }
    }
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);
    doc.text("Authorized Signatory", MARGIN_RIGHT, rowStartY + 13, {
      align: "right",
    });
    if (storeSettings?.negobillBranding) {
      const y = pageH - 5;
      const rightX = pageW / 2 - 10;

      // Text parts
      const normalText = "Powered by ";
      const boldText = "NegoBill.com";

      doc.setFontSize(8);

      // Measure widths
      doc.setFont("Inter-Regular", "normal");
      const normalW = doc.getTextWidth(normalText);

      doc.setFont("Inter-Bold", "bold");

      // Start X so whole string is right-aligned
      const startX = rightX;

      // Draw normal part
      doc.setFont("Inter-Regular", "normal");
      doc.text(normalText, startX, y, {
        align: "center",
      });

      // Draw bold part
      doc.setFont("Inter-Bold", "bold");
      doc.text(boldText, startX + 1 + normalW, y, {
        align: "center",
      });
    }
    setDownload(false);
    return doc;
  };

  if (isReturnView) {
    return (
      <ReturnSaleInvoice
        isData={returnData} // This is the updated return sale data loaded by your hook
        selectedOption={selectedOption}
        saleId={isData?._id}
        onClose={handleClose}
        onDataUpdate={onDataUpdate}
        onDataChange={onDataChange}
        isActive={isActive}
      />
    );
  }
  return (
    <>
      <InvoiceModalLayout
        isOpen={isMainModalOpen}
        data={data}
        generateInvoice1={generateInvoice1}
        generateBill1={generateBill1}
        latestVersion={latestVersion}
        isBtnLoading={isBtnLoading}
        handleResetSale={handleResetSale}
        handleModeConfirm={handleModeConfirm}
        onClose={handleClose}
        {...invoiceData}
      >
        <InvoiceToggleActionCard
          optData={optData}
          setOptData={setOptData}
          setBilling={setBilling}
          setSelectTermalOption={setSelectTermalOption}
          setSelectHALFA4Option={setSelectHALFA4Option}
        />
        {saleLoading ? (
          <div
            className="d-flex justify-content-center"
            style={{
              overflowY: "auto",
              maxHeight: "100%",
              width: "100%",
              paddingRight: "12px",
              paddingLeft: "12px",
            }}
          >
            <CircularProgress size={20} className="text-primary" />
          </div>
        ) : (
          <div
            className="pt-2"
            style={{
              overflowY: "auto",
              maxHeight: "100%",
              width: "100%",
              paddingRight: "12px",
              paddingLeft: "12px",
            }}
          >
            {globalData?.salemode === "INVOICE" || isBillReceipt ? (
              <>
                {selectedSize === "HALFA4" ? (
                  <>
                    <InvoicePreviewRenderer
                      selectedSize={selectedSize}
                      selectHALFA4Option={selectHALFA4Option}
                      data={data}
                      optData={optData}
                      selectedTheme={selectedTheme}
                    />
                  </>
                ) : (
                  <div className="tm_container">
                    <div className="tm_invoice_wrap">
                      <div className="tm_invoice tm_style1 tm_type1">
                        <div className="tm_invoice_in p-2">
                          <div className="d-flex justify-content-between mb-2">
                            <div className="tm_logo text-start">
                              {globalData?.salefrom?.image && (
                                <img
                                  src={
                                    uRLs.IMAGE_URL +
                                    "profile/" +
                                    globalData?.salefrom?.image
                                  }
                                  className="img-rounded logo mb-0"
                                  height="45"
                                  width="150"
                                  style={{ objectFit: "contain" }}
                                />
                              )}
                            </div>
                            <div className="text-end">
                              <div className="font-16">
                                {documentTitle}
                                <div>
                                  <span className="font-12 fw-600">
                                    {invLabel}
                                  </span>
                                  {""}
                                  <strong className="font-12">
                                    {globalData?.saleno}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-5 pe-1 ">
                              <div className="d-flex gap-2 align-items-start">
                                <div>
                                  <div className="font-14 mb- fw-bold">
                                    {globalData.salefrom?.title || ""}
                                  </div>
                                  {globalData?.salefrom?.gstnumber && (
                                    <div className="font-11 fw-600 mb-0 ">
                                      <>GSTIN:</>{" "}
                                      {globalData?.salefrom?.gstnumber || ""}
                                    </div>
                                  )}
                                  <div className="font-11">
                                    {globalData?.salefrom?.addressline1 || ""}{" "}
                                    {globalData?.salefrom?.addressline2 || ""}{" "}
                                    {globalData?.salefrom?.district || ""}
                                    {", "}
                                    {globalData?.salefrom?.state || ""}
                                    {", "}
                                    {globalData?.salefrom?.pincode || ""}
                                  </div>
                                  <div className="font-11">
                                    <strong>Mobile:</strong>{" "}
                                    {globalData?.salefrom?.phone || ""} |{" "}
                                    <strong>Email:</strong>{" "}
                                    {globalData?.salefrom?.email || "-"}
                                  </div>
                                  {globalData?.salefrom?.pannumber && (
                                    <div className="font-11">
                                      <strong>PAN:</strong>{" "}
                                      {globalData?.salefrom?.pannumber || ""}
                                    </div>
                                  )}
                                  {globalData?.salefrom?.fssai && (
                                    <div className="font-11">
                                      <strong>FASSI:</strong>{" "}
                                      {globalData?.salefrom?.fssai || ""}
                                    </div>
                                  )}
                                  {globalData?.salefrom?.cinnumber && (
                                    <div className="font-11">
                                      <strong>CIN:</strong>{" "}
                                      {globalData?.salefrom?.cinnumber}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row mt-2">
                            <div className="col-4 pe-1">
                              {globalData?.saleto &&
                                optData?.isCustomerDisplay && (
                                  <>
                                    <div>
                                      <div>
                                        <span className="fw-500">Bill To:</span>
                                        {""}
                                      </div>
                                      <div className="font-12 mb-0">
                                        <strong>
                                          {globalData?.saleto?.name || ""}
                                        </strong>
                                      </div>
                                      {globalData?.saleto?.gstnumber && (
                                        <div className="font-11">
                                          <strong>GSTIN:</strong>{" "}
                                          {globalData.saleto?.gstnumber || ""}
                                        </div>
                                      )}
                                      <div className="font-11">
                                        {globalData?.saleto?.addressline1 || ""}{" "}
                                        {globalData?.saleto?.addressline2 ||
                                          ""}{" "}
                                      </div>
                                      <div className="font-11">
                                        {globalData?.saleto?.state || ""}{" "}
                                        {globalData?.saleto?.district || ""}{" "}
                                        {globalData?.saleto?.country || ""}{" "}
                                        {globalData?.saleto?.pincode || ""}
                                      </div>

                                      {globalData?.saleto?.phone && (
                                        <div className="font-11">
                                          {" "}
                                          Ph: {globalData?.saleto?.phone ||
                                            ""}{" "}
                                          | Email:{" "}
                                          {globalData?.saleto?.email || ""}
                                        </div>
                                      )}
                                      {/* <div> Email {globalData.saleto?.email || ""}</div> */}
                                      {globalData.saleto?.referenceid && (
                                        <div className="font-11">
                                          Reference No:{" "}
                                          {globalData.saleto?.referenceid || ""}
                                        </div>
                                      )}
                                      {globalData.saleto?.pannumber && (
                                        <div className="font-11">
                                          <strong>PAN:</strong>{" "}
                                          {globalData.saleto?.pannumber || ""}
                                        </div>
                                      )}
                                      {globalData.saleto?.fssai && (
                                        <div className="font-11">
                                          <strong>FASSI:</strong>{" "}
                                          {globalData.saleto?.fssai || ""}
                                        </div>
                                      )}
                                      {globalData.saleto?.cinnumber && (
                                        <div className="font-11">
                                          <strong>CIN :</strong>
                                          {globalData?.saleto?.cinnumber || ""}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                            </div>
                            {optData?.isCustomerDisplay &&
                              globalData.saleto?.shippingAddress &&
                              (globalData.saleto?.shippingAddress
                                ?.addressline1 ||
                                globalData.saleto?.shippingAddress
                                  ?.addressline2) && (
                                <>
                                  <div className="col-5 pe-1 ms-auto">
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        // fontWeight: "600",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Shipping Address
                                    </div>
                                    <div>
                                      {globalData?.saleto?.shippingAddress
                                        ?.addressline1 || ""}{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.addressline2 || ""}{" "}
                                    </div>
                                    <div>
                                      {globalData?.saleto?.shippingAddress
                                        ?.state || ""}{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.district || ""}{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.country || ""}{" "}
                                      -{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.pincode || ""}
                                    </div>
                                  </div>
                                </>
                              )}
                            <div className="col-3 ps-1 ms-auto">
                              {saleTime.date && (
                                <>
                                  {/* justify-content-between pushes the value to the right end */}
                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">{invDate}</span>
                                    <strong className="fw-semibold">
                                      {saleTime.date}
                                    </strong>
                                  </div>

                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">{invTimes}</span>
                                    <strong className="fw-semibold">
                                      {
                                        DateConverter.ISOToDisplayFormat(
                                          DateConverter.convertToTimeZone(
                                            globalData?.saledate,
                                            isUser.timezone,
                                          ),
                                        ).time
                                      }
                                    </strong>
                                  </div>
                                </>
                              )}
                              {globalData?.saleto?.state && (
                                <>
                                  <div className="d-flex  font-11 justify-content-between mb-10">
                                    <span className="fw-bold">
                                      Place of Supply:
                                    </span>
                                    <strong className="fw-semibold text-end ">
                                      {globalData?.saleto?.state || ""}
                                    </strong>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <table
                            // className="items"
                            width="100%"
                            style={{
                              marginTop: "2px",
                              borderCollapse: "collapse",
                              // borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              // borderBottom: "1px solid #000"
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr
                              //  style={{
                              //   borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              //   borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`
                              // }}
                              >
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "20px",
                                    textAlign: "center",
                                  }}
                                >
                                  #
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    // width: "240px",
                                    textAlign: "left",
                                  }}
                                >
                                  Item
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "50px",
                                    textAlign: "right",
                                  }}
                                >
                                  MRP
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "right",
                                  }}
                                >
                                  Selling Price
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "right",
                                  }}
                                >
                                  Qty
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "right",
                                  }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {globalData?.saleitems?.map((product, index) => (
                                <tr
                                  className="align-top font-11"
                                  key={index}
                                  style={{
                                    borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <td className="force-style align-left">
                                    {index + 1}
                                  </td>
                                  <td className="force-style align-left">
                                    <div className="font-11">
                                      {product.title}
                                    </div>

                                    {product.hsnsac && (
                                      <div>
                                        <span className="fw-medium font-11">
                                          {" "}
                                          {product.imode === "SERVICE"
                                            ? "SAC:"
                                            : "HSN:"}
                                        </span>{" "}
                                        {product.hsnsac}
                                      </div>
                                    )}
                                  </td>
                                  <td className="force-style text-end">
                                    ₹{product?.mrp}
                                  </td>
                                  <td className="force-style text-end">
                                    ₹{product?.baseprice}
                                  </td>
                                  <td className="force-style text-end">
                                    {product?.quantity}
                                    <span className="font-11 ms-1">
                                      {(() => {
                                        const unitCode =
                                          product?.isSecondaryUnit
                                            ? product?.unitConfig
                                                ?.secondaryUnitCode
                                            : product?.unitConfig
                                                ?.primaryUnitCode;

                                        // return unitCode !== "unit" ? unitCode : "";
                                        return unitCode || "";
                                      })()}
                                    </span>
                                  </td>

                                  <td className="force-style text-end">
                                    ₹{product?.lineGrandTotal}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="row mt-2">
                            <div className="col-6 pe-1">
                              <div className="font-12">
                                {storeSettings?.paymentSettings?.bankDetails &&
                                  optData?.isBankDetails && (
                                    <>
                                      <strong className="mb-1">
                                        Bank Details:
                                      </strong>
                                      <br />

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Name:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.bankDetails?.beneficiaryName || ""}
                                      </div>

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Account #:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.bankDetails?.accountNo || ""}
                                      </div>

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          IFSC:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.bankDetails?.ifsc || ""}
                                      </div>

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          UPI ID:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.upiId || ""}
                                      </div>
                                    </>
                                  )}
                                <div className="mt-2 font-11">
                                  {`Total amount (in words) ${
                                    globalData?.amountinwords || ""
                                  }`}
                                </div>
                                {optData?.isPayment && (
                                  <div className=" font-11">
                                    {globalData?.cashPaid > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>₹{globalData.cashPaid}</b> Paid via
                                          Cash
                                        </span>
                                      </div>
                                    )}
                                    {globalData?.upiPaid > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>₹{globalData.upiPaid}</b> Paid via
                                          UPI{" "}
                                        </span>
                                      </div>
                                    )}
                                    {globalData?.cardPaid > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>₹{globalData.cardPaid}</b> Paid via
                                          Card{" "}
                                        </span>
                                      </div>
                                    )}
                                    {globalData?.onlinePaid > 0 && (
                                      <div className=" font-10 ps-1">
                                        <span>
                                          <b>₹{globalData.onlinePaid}</b> Paid
                                          via Online{" "}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* CENTER – QR CODE */}
                            <div className="col-2 pe-1 text-center">
                              {storeSettings?.paymentSettings?.upiId &&
                                optData?.isBankDetails && (
                                  <img
                                    src={qrCodeDataUrl}
                                    alt="QR Code"
                                    style={{
                                      width: "90px",
                                      height: "90px",
                                    }}
                                  />
                                )}
                            </div>

                            <div className="col-4 pe-1">
                              <div className="font-11">
                                <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                  <span>Taxable Amount</span>
                                  <span>₹{globalData?.totalbaseprice} </span>
                                </div>
                                {globalData?.salefrom?.statecode &&
                                globalData?.saleto?.statecode &&
                                globalData?.salefrom?.statecode !==
                                  globalData?.saleto?.statecode ? (
                                  <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                    <span>IGST</span>
                                    <span>₹{globalData?.igstamount} </span>
                                  </div>
                                ) : (
                                  <>
                                    <div className="d-flex justify-content-between   font-11 pe-sm-0-6">
                                      <span>CGST</span>
                                      <span>₹{globalData?.cgstamount} </span>
                                    </div>

                                    <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                      <span>SGST</span>
                                      <span>₹{globalData?.sgstamount} </span>
                                    </div>
                                  </>
                                )}
                                <div
                                  className="d-flex justify-content-between fw-bold mt-1 font-11 pe-sm-0-6"
                                  style={{
                                    backgroundColor:
                                      selectedTheme?.templateTheme
                                        ?.primaryColor, // light grey/blue
                                    padding: "4px 8px",

                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    borderRadius: "0px",
                                  }}
                                >
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    Total
                                  </span>
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    ₹{globalData?.grandtotal}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between fw-bold font-11 pe-sm-0-6">
                                  <span>Amount Payable:</span>
                                  <span>₹{globalData?.balanceDue}</span>
                                </div>
                                <div className="d-flex justify-content-between font-11 pe-sm-0-6">
                                  <span>Amount paid:</span>
                                  <span>
                                    ₹
                                    {parseFloat(actualPaidAmount || 0).toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {optData?.isRemark && globalData?.remark && (
                            <table width="100%" className="mb-2 mt-2">
                              <tbody>
                                <tr>
                                  <td width="100%">
                                    <div className="font-12 fw-600 align-left text-black mb-1">
                                      Remark
                                    </div>

                                    <div
                                      className="font-12 p-0"
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        minHeight: "30px",
                                      }}
                                    >
                                      {capitalizeFirstLetter(
                                        globalData?.remark,
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                          <div className="row mt-2">
                            <div className="col-9 pe-1">
                              {terms?.length > 0 && (
                                <table width="100%">
                                  <tbody>
                                    <tr>
                                      <td width="100%">
                                        <div
                                          className="fs-12 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box font-10">
                                          {terms}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                            <div className="col-3 pe-2">
                              <div>
                                {storeSettings?.invoiceSettings?.signature && (
                                  <div className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings
                                          ?.signature
                                      }
                                      width={130}
                                    />
                                  </div>
                                )}
                                <div className="text-end p-0">
                                  Authorized Signatory
                                </div>
                                {/* <div className="text-end p-0 fw-bold font-14">
                                          {data?.salefrom?.title}
                                        </div> */}
                              </div>
                            </div>
                          </div>
                          {storeSettings?.negobillBranding && (
                            <div className="d-flex justify-content-center">
                              {" "}
                              <span>
                                Powered by <strong>NegoBill.com</strong>
                              </span>
                            </div>
                          )}

                          {/* <table
                            width="100%"
                            style={{ borderCollapse: "collapse" }}
                            cellPadding="8"
                          >
                            <tbody>
                            <tr>
                            {storeSettings?.invoiceSettings?.signature && (
                                  <td className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings?.signature
                                      }
                                      width={150}
                                    />
                                  </td> )}
          
                              </tr>
                              <tr>
                                <td className="text-end p-0">Issued By,Signature</td>
                              </tr>
                              <tr>
                                <td className="text-end p-0 fw-bold ">
                                  {settings?.saleProfileData?.title}
                                </td>
                              </tr>
                            </tbody>
                          </table> */}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : globalData.salemode === "BILL" ? (
              <>
                {selectedSize === "HALFA4" ? (
                  <>
                    <InvoicePreviewRenderer
                      selectedSize={selectedSize}
                      selectHALFA4Option={selectHALFA4Option}
                      data={data}
                      optData={optData}
                      selectedTheme={selectedTheme}
                    />
                  </>
                ) : (
                  <div className="tm_container">
                    <div className="tm_invoice_wrap">
                      <div className="tm_invoice tm_style1 tm_type1">
                        <div className="tm_invoice_in p-2">
                          <div className="d-flex justify-content-between mb-2">
                            <div className="tm_logo text-start">
                              {globalData?.salefrom?.image && (
                                <img
                                  src={
                                    uRLs.IMAGE_URL +
                                    "profile/" +
                                    globalData?.salefrom?.image
                                  }
                                  className="img-rounded logo mb-0"
                                  height="45"
                                  width="150"
                                  style={{ objectFit: "contain" }}
                                />
                              )}
                            </div>
                            <div className="text-end">
                              <div className="font-16">
                                BILL RECEIPT
                                <div>
                                  <span className="font-12 fw-600">
                                    Bill #:
                                  </span>
                                  {""}
                                  <strong className="font-12">
                                    {globalData.saleno}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4 pe-1 ">
                              <div className="d-flex gap-2 align-items-start">
                                <div>
                                  <div className="font-14 mb-1 fw-bold">
                                    {globalData.salefrom?.title || ""}
                                  </div>
                                  {globalData?.salefrom?.gstnumber && (
                                    <div className="font-12 fw-600 mb-0">
                                      <>GSTIN:</>{" "}
                                      {globalData?.salefrom?.gstnumber || ""}
                                    </div>
                                  )}
                                  <div className="font-11">
                                    {globalData?.salefrom?.addressline1 || ""}{" "}
                                    {globalData?.salefrom?.addressline2 || ""}{" "}
                                    {globalData?.salefrom?.district || ""}
                                    {", "}
                                    {globalData?.salefrom?.state || ""}
                                    {", "}
                                    {globalData?.salefrom?.pincode || ""}
                                  </div>
                                  <div font-11>
                                    <strong>Mobile:</strong>{" "}
                                    {globalData?.salefrom?.phone || ""} |{" "}
                                    <strong>Email:</strong>{" "}
                                    {globalData?.salefrom?.email || "-"}
                                  </div>
                                  {globalData?.salefrom?.pannumber && (
                                    <div>
                                      <strong>PAN:</strong>{" "}
                                      {globalData?.salefrom?.pannumber || ""}
                                    </div>
                                  )}
                                  {globalData?.salefrom?.fssai && (
                                    <div>
                                      <strong>FASSI:</strong>{" "}
                                      {globalData?.salefrom?.fssai || ""}
                                    </div>
                                  )}
                                  {globalData?.salefrom?.cinnumber && (
                                    <div>
                                      <strong>CIN:</strong>{" "}
                                      {globalData?.salefrom?.cinnumber}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="row mt-2 mb-1">
                            <div className="col-4 pe-1">
                              {globalData?.saleto &&
                                optData?.isCustomerDisplay && (
                                  <>
                                    <div>
                                      <div>
                                        <span className="fw-500">Bill To:</span>
                                        {""}
                                      </div>
                                      <div className="font-12 mb-0">
                                        <strong>
                                          {globalData?.saleto?.name || ""}
                                        </strong>
                                      </div>
                                      {globalData?.saleto?.gstnumber && (
                                        <div className="font-11">
                                          <strong>GSTIN:</strong>{" "}
                                          {globalData.saleto?.gstnumber || ""}
                                        </div>
                                      )}
                                      <div className="font-11">
                                        {globalData?.saleto?.addressline1 || ""}{" "}
                                        {globalData?.saleto?.addressline2 ||
                                          ""}{" "}
                                      </div>
                                      <div className="font-11">
                                        {globalData?.saleto?.state || ""}{" "}
                                        {globalData?.saleto?.district || ""}{" "}
                                        {globalData?.saleto?.country || ""}{" "}
                                        {globalData?.saleto?.pincode || ""}
                                      </div>
                                      {/* {globalData?.saleto?.gstnumber && (
                                          <div className="font-11">
                                            <strong>GSTIN:</strong>{" "}
                                            {globalData.saleto?.gstnumber || ""}
                                          </div>
                                           )}  */}
                                      {globalData?.saleto?.phone && (
                                        <div className="font-11">
                                          Ph: {globalData?.saleto?.phone || ""}{" "}
                                          | Email:{" "}
                                          {globalData?.saleto?.email || ""}
                                        </div>
                                      )}
                                      {/* <div> Email {globalData.saleto?.email || ""}</div> */}
                                      {globalData.saleto?.referenceid && (
                                        <div className="font-11">
                                          Reference No:{" "}
                                          {globalData.saleto?.referenceid || ""}
                                        </div>
                                      )}
                                      {globalData.saleto?.pannumber && (
                                        <div className="font-11">
                                          <strong>PAN:</strong>{" "}
                                          {globalData.saleto?.pannumber || ""}
                                        </div>
                                      )}
                                      {globalData?.salefrom?.fssai && (
                                        <div>
                                          <strong>FASSI:</strong>{" "}
                                          {globalData?.salefrom?.fssai || ""}
                                        </div>
                                      )}
                                      {globalData.saleto?.cinnumber && (
                                        <div className="font-11">
                                          <strong>CIN :</strong>
                                          {globalData?.saleto?.cinnumber || ""}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                            </div>
                            {optData?.isCustomerDisplay &&
                              globalData.saleto?.shippingAddress &&
                              (globalData.saleto?.shippingAddress
                                ?.addressline1 ||
                                globalData.saleto?.shippingAddress
                                  ?.addressline2) && (
                                <>
                                  <div className="col-5 pe-1">
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        // fontWeight: "600",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Shipping Address
                                    </div>
                                    <div>
                                      {globalData?.saleto?.shippingAddress
                                        ?.addressline1 || ""}{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.addressline2 || ""}{" "}
                                    </div>
                                    <div>
                                      {globalData?.saleto?.shippingAddress
                                        ?.state || ""}{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.district || ""}{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.country || ""}{" "}
                                      -{" "}
                                      {globalData?.saleto?.shippingAddress
                                        ?.pincode || ""}
                                    </div>
                                  </div>
                                </>
                              )}
                            <div className="col-3 ps-1 ms-auto">
                              {/* justify-content-between pushes the value to the right end */}
                              {saleTime.date && (
                                <>
                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">Bill Date:</span>
                                    <strong className="fw-semibold">
                                      {saleTime.date}
                                    </strong>
                                  </div>

                                  <div className="d-flex justify-content-between mb-0 font-11">
                                    <span className="fw-bold">Bill Time:</span>
                                    <strong className="fw-semibold">
                                      {
                                        DateConverter.ISOToDisplayFormat(
                                          DateConverter.convertToTimeZone(
                                            globalData?.saledate,
                                            isUser.timezone,
                                          ),
                                        ).time
                                      }
                                    </strong>
                                  </div>
                                </>
                              )}
                              {globalData?.saleto?.state && (
                                <div className="d-flex justify-content-between mb-10 font-11">
                                  <span className="fw-bold">
                                    Place of Supply:
                                  </span>
                                  <strong className="fw-semibold text-end">
                                    {globalData?.saleto?.state || ""}
                                  </strong>
                                </div>
                              )}
                            </div>
                          </div>

                          <table
                            // className="items"
                            width="100%"
                            style={{
                              marginTop: "2px",
                              borderCollapse: "collapse",
                              // borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              // borderBottom: "1px solid #000"
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr
                              //  style={{
                              //   borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                              //   borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`
                              // }}
                              >
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "20px",
                                    textAlign: "left",
                                  }}
                                >
                                  #
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "auto",
                                    textAlign: "left",
                                  }}
                                >
                                  Item
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "right",
                                  }}
                                >
                                  Selling Price
                                </th>
                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "50px",
                                    textAlign: "right",
                                  }}
                                >
                                  Qty
                                </th>

                                <th
                                  className="force-style p-1 fw-bold"
                                  style={{
                                    width: "100px",
                                    textAlign: "right",
                                  }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {globalData?.saleitems?.map((product, index) => (
                                <tr
                                  key={index}
                                  className="align-top font-11"
                                  style={{
                                    borderTop: `1px solid ${selectedTheme?.templateTheme?.borderColor} `,
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                  }}
                                >
                                  <td className="force-style align-left">
                                    {index + 1}
                                  </td>
                                  <td className="force-style align-left ">
                                    <div className="font-11">
                                      {product.title}
                                    </div>

                                    {product.hsnsac && (
                                      <div>
                                        <span className="fw-medium font-11">
                                          {" "}
                                          {product.imode === "SERVICE"
                                            ? "SAC:"
                                            : "HSN:"}
                                        </span>{" "}
                                        {product.hsnsac}
                                      </div>
                                    )}
                                  </td>

                                  <td className="force-style text-end">
                                    ₹{product?.baseprice}
                                  </td>
                                  <td className="force-style text-end">
                                    {product?.quantity}
                                    <span className="font-11 ms-1">
                                      {(() => {
                                        const unitCode =
                                          product?.isSecondaryUnit
                                            ? product?.unitConfig
                                                ?.secondaryUnitCode
                                            : product?.unitConfig
                                                ?.primaryUnitCode;

                                        // return unitCode !== "unit" ? unitCode : "";
                                        return unitCode || "";
                                      })()}
                                    </span>
                                  </td>

                                  <td className="force-style text-end">
                                    {" "}
                                    ₹{product?.lineGrandTotal}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="row mt-2">
                            <div className="col-6 pe-1">
                              <div className="font-12">
                                {storeSettings?.paymentSettings?.bankDetails &&
                                  optData?.isBankDetails && (
                                    <>
                                      <strong className="mb-1">
                                        Bank Details:
                                      </strong>
                                      <br />

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Name:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.bankDetails?.beneficiaryName || ""}
                                      </div>

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          Account #:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.bankDetails?.accountNo || ""}
                                      </div>

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          IFSC:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.bankDetails?.ifsc || ""}
                                      </div>

                                      <div className="d-flex font-11">
                                        <span style={{ width: "120px" }}>
                                          UPI ID:
                                        </span>

                                        {storeSettings?.paymentSettings
                                          ?.upiId || ""}
                                      </div>
                                    </>
                                  )}
                                <div className="mt-2 font-11">
                                  {`Total amount (in words) ${
                                    globalData?.amountinwords || ""
                                  }`}
                                </div>
                                {optData?.isPayment && (
                                  <div className=" font-11">
                                    {globalData?.cashPaid > 0 && (
                                      <div className=" font-10 ">
                                        <span>
                                          <b>₹{globalData.cashPaid}</b> Paid via
                                          Cash
                                        </span>
                                      </div>
                                    )}
                                    {globalData?.upiPaid > 0 && (
                                      <div className=" font-10 ">
                                        <span>
                                          <b>₹{globalData.upiPaid}</b> Paid via
                                          UPI{" "}
                                        </span>
                                      </div>
                                    )}
                                    {globalData?.cardPaid > 0 && (
                                      <div className=" font-10">
                                        <span>
                                          <b>₹{globalData.cardPaid}</b> Paid via
                                          Card{" "}
                                        </span>
                                      </div>
                                    )}
                                    {globalData?.onlinePaid > 0 && (
                                      <div className=" font-10 ">
                                        <span>
                                          <b>₹{globalData.onlinePaid}</b> Paid
                                          via Online{" "}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* CENTER – QR CODE */}

                            <div className="col-2 pe-1 text-center">
                              {storeSettings?.paymentSettings?.upiId &&
                                optData?.isBankDetails && (
                                  <img
                                    src={qrCodeDataUrl}
                                    alt="QR Code"
                                    style={{
                                      width: "90px",
                                      height: "90px",
                                    }}
                                  />
                                )}
                            </div>
                            <div className="col-4 pe-1">
                              <div className="font-11">
                                <div className="d-flex justify-content-between  font-11 pe-sm-0-6">
                                  <span>Taxable Amount</span>
                                  <span>₹{globalData?.totalbaseprice} </span>
                                </div>

                                <div
                                  className="d-flex justify-content-between fw-bold mt-2 font-11 pe-sm-0-6 "
                                  style={{
                                    backgroundColor:
                                      selectedTheme?.templateTheme
                                        ?.primaryColor, // light grey/blue
                                    padding: "4px 8px",

                                    border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    borderRadius: "0px",
                                  }}
                                >
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    Total
                                  </span>
                                  <span
                                    style={{
                                      color:
                                        selectedTheme?.templateTheme
                                          ?.foregroundColor,
                                    }}
                                  >
                                    ₹{globalData?.grandtotal}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between fw-bold mt-1 font-11 pe-sm-0-6">
                                  <span>Amount Payable:</span>
                                  <span>{globalData?.balanceDue}</span>
                                </div>
                                <div className="d-flex justify-content-between font-11 pe-sm-0-6">
                                  <span>Amount paid:</span>
                                  <span>
                                    ₹
                                    {parseFloat(actualPaidAmount || 0).toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {optData?.isRemark && globalData?.remark && (
                            <table width="100%" className="mb-2 mt-2">
                              <tbody>
                                <tr>
                                  <td width="100%">
                                    <div className="font-12 fw-600 align-left text-black mb-1">
                                      Remark
                                    </div>

                                    <div
                                      className="font-12 p-0"
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        minHeight: "30px",
                                      }}
                                    >
                                      {capitalizeFirstLetter(
                                        globalData?.remark,
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                          <div className="row mt-2">
                            <div className="col-9 pe-1">
                              {terms?.length > 0 && (
                                <table width="100%">
                                  <tbody>
                                    <tr>
                                      <td width="100%">
                                        <div
                                          className="fs-12 fw-600"
                                          style={{
                                            textAlign: "left",
                                            color: "black",
                                          }}
                                        >
                                          Terms & Conditions
                                        </div>
                                        <div className="terms-box font-10">
                                          {terms}
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                            </div>
                            <div className="col-3 pe-2">
                              <div>
                                {storeSettings?.invoiceSettings?.signature && (
                                  <div className="text-end p-0 fw-bold ">
                                    <img
                                      src={
                                        uRLs.IMAGE_URL +
                                        storeSettings?.invoiceSettings
                                          ?.signature
                                      }
                                      width={130}
                                    />
                                  </div>
                                )}
                                <div className="text-end p-0">
                                  Authorized Signatory
                                </div>
                                {/* <div className="text-end p-0 fw-bold font-14">
                                          {data?.salefrom?.title}
                                        </div> */}
                              </div>
                            </div>
                          </div>
                          {storeSettings?.negobillBranding && (
                            <div className="d-flex justify-content-center">
                              {" "}
                              <span>
                                Powered by <strong>NegoBill.com</strong>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="default-content">
                <p>No content available for this sale mode.</p>
              </div>
            )}
           
          </div>
        )}
        <InvoiceInfoSidebar
          {...invoiceData}
          isData={isData}
          handleSelectedReturnSale={handleSelectedReturnSale}
        />
      </InvoiceModalLayout>
      {showConfirm && (
        <Convertinvoice
          onClose={() => setShowConfirm(false)}
          onConfirm={() => handleGenerateInvoice(data)}
        />
      )}

      <PaySettleModal
        isOpen={isPaySettleModalOpen}
        data={data}
        setIsPaySettleModalOpen={setIsPaySettleModalOpen}
        setIsMainModalOpen={setIsMainModalOpen}
        isBtnLoading={isBtnLoading}
        setBtnLoading={setBtnLoading}
      />
    </>
  );
};

export default ServiceInvoice;
